import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, Calendar, MapPin, Star, Film, ChevronDown, X, RotateCcw } from 'lucide-react';
import { getPersonDetails, type PersonDetails as PersonDetailsType } from '../api/tmdb';
import { getProfileUrl, getPosterUrl } from '../utils/helpers';
import { GENRES } from '../utils/constants';

type PersonFilterTab = 'genre' | 'rating' | 'year' | null;

export function PersonDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [person, setPerson] = useState<PersonDetailsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states for Known For section
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [yearRange, setYearRange] = useState<[number, number]>([1920, new Date().getFullYear()]);
  const [minRating, setMinRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'rating' | 'date' | 'title'>('rating');
  const [activeFilterTab, setActiveFilterTab] = useState<PersonFilterTab>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchPerson() {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getPersonDetails(parseInt(id));
        setPerson(data);
      } catch (err) {
        console.error('Error fetching person:', err);
        setError('Failed to load person details');
      } finally {
        setLoading(false);
      }
    }

    fetchPerson();
  }, [id]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setActiveFilterTab(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get all movies (filter out TV shows) - must be before early returns
  const allMovies = useMemo(() => {
    if (!person) return [];
    return person.combined_credits?.cast
      .filter((credit) => credit.media_type === 'movie' && credit.title) || [];
  }, [person]);

  // Available genres from this person's movies
  const availableGenres = useMemo(() => {
    const genreSet = new Set<number>();
    allMovies.forEach(movie => {
      (movie as unknown as { genre_ids?: number[] }).genre_ids?.forEach(id => genreSet.add(id));
    });
    return Array.from(genreSet).filter(id => GENRES[id]);
  }, [allMovies]);

  // Filtered and sorted movies
  const movies = useMemo(() => {
    let filtered = allMovies.filter(movie => {
      // Year filter
      const year = movie.release_date ? parseInt(movie.release_date.split('-')[0]) : null;
      if (year && (year < yearRange[0] || year > yearRange[1])) return false;

      // Rating filter
      if (movie.vote_average < minRating) return false;

      // Genre filter - note: cast credits don't have genre_ids, so we skip if no genres selected
      // or if the movie doesn't have genre info
      if (selectedGenres.length > 0) {
        const movieGenres = (movie as unknown as { genre_ids?: number[] }).genre_ids || [];
        if (!selectedGenres.some(g => movieGenres.includes(g))) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.vote_average || 0) - (a.vote_average || 0);
        case 'date':
          return (b.release_date || '').localeCompare(a.release_date || '');
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });

    return filtered.slice(0, 48);
  }, [allMovies, yearRange, minRating, selectedGenres, sortBy]);

  // Get directed movies
  const directedMovies = useMemo(() => {
    if (!person) return [];
    return person.combined_credits?.crew
      .filter((credit) => credit.media_type === 'movie' && credit.job === 'Director' && credit.title)
      .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
      .slice(0, 12) || [];
  }, [person]);

  // Check if any filters are active
  const hasActiveFilters = selectedGenres.length > 0 || minRating > 0 || yearRange[0] > 1920 || yearRange[1] < new Date().getFullYear();

  // Clear all filters
  const clearFilters = () => {
    setSelectedGenres([]);
    setYearRange([1920, new Date().getFullYear()]);
    setMinRating(0);
  };

  // Toggle genre
  const toggleGenre = (genreId: number) => {
    setSelectedGenres(prev =>
      prev.includes(genreId) ? prev.filter(g => g !== genreId) : [...prev, genreId]
    );
  };

  // Calculate age
  const calculateAge = () => {
    if (!person || !person.birthday) return null;
    const birth = new Date(person.birthday);
    const end = person.deathday ? new Date(person.deathday) : new Date();
    let age = end.getFullYear() - birth.getFullYear();
    const monthDiff = end.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && end.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading person details...</p>
        </div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Person not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation buttons */}
      <div className="bg-slate-800/50 border-b border-slate-700/50">
        <div className="max-w-screen-xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-full text-slate-300 hover:text-white transition-all"
            >
              <ArrowLeft size={18} />
              <span className="text-sm">Back</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-full text-slate-300 hover:text-cyan-400 transition-all"
            >
              <Home size={18} />
              <span className="text-sm">Home</span>
            </button>
          </div>
        </div>
      </div>

      {/* Person info */}
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Profile image */}
          <div className="flex-shrink-0">
            <div className="w-64 aspect-[2/3] rounded-xl overflow-hidden shadow-2xl bg-slate-800">
              {person.profile_path ? (
                <img
                  src={getProfileUrl(person.profile_path, 'large')}
                  alt={person.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">
                  <span className="text-6xl">👤</span>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1">
            {/* Name */}
            <h1 className="text-4xl font-bold text-white mb-2">{person.name}</h1>

            {/* Known for */}
            <p className="text-lg text-cyan-400 mb-4">{person.known_for_department}</p>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-slate-300 mb-6">
              {person.birthday && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={16} className="text-slate-500" />
                  <span>
                    {new Date(person.birthday).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                    {age && !person.deathday && ` (${age} years old)`}
                  </span>
                </div>
              )}
              {person.deathday && (
                <div className="flex items-center gap-1.5 text-red-400">
                  <span>
                    Died: {new Date(person.deathday).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                    {age && ` (${age} years old)`}
                  </span>
                </div>
              )}
              {person.place_of_birth && (
                <div className="flex items-center gap-1.5">
                  <MapPin size={16} className="text-slate-500" />
                  <span>{person.place_of_birth}</span>
                </div>
              )}
            </div>

            {/* Biography */}
            {person.biography && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-2">Biography</h2>
                <p className="text-slate-300 leading-relaxed whitespace-pre-line">
                  {person.biography.length > 1000
                    ? `${person.biography.slice(0, 1000)}...`
                    : person.biography}
                </p>
              </div>
            )}

            {/* Also known as */}
            {person.also_known_as && person.also_known_as.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Also Known As</h3>
                <div className="flex flex-wrap gap-2">
                  {person.also_known_as.slice(0, 5).map((name, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-sm"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Directed Movies */}
        {directedMovies.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Film size={24} className="text-cyan-400" />
              Directed Movies
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {directedMovies.map((movie) => (
                <Link
                  key={`dir-${movie.id}`}
                  to={`/movie/${movie.id}`}
                  className="group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-800 mb-2 relative">
                    {movie.poster_path ? (
                      <img
                        src={getPosterUrl(movie.poster_path, 'medium')}
                        alt={movie.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <span className="text-4xl">🎬</span>
                      </div>
                    )}
                    {movie.vote_average > 0 && (
                      <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded flex items-center gap-1">
                        <Star size={12} className="text-yellow-400" />
                        <span className="text-white text-xs font-bold">{movie.vote_average.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                    {movie.title}
                  </h3>
                  {movie.release_date && (
                    <p className="text-xs text-slate-500">{movie.release_date.split('-')[0]}</p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Movies (Acting) */}
        {allMovies.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2 mb-6">
              <Star size={24} className="text-yellow-400" />
              Known For
            </h2>
          </div>
        )}
      </div>

      {/* Filter Bar - Full Width like home page */}
      {allMovies.length > 0 && (
        <div ref={filterDropdownRef} className="relative">
          {/* Filter Tabs */}
          <div className="bg-gradient-to-r from-teal-600 via-yellow-500 to-orange-500">
            <div className="max-w-screen-xl mx-auto">
              <div className="flex items-center justify-center overflow-x-auto scrollbar-hide">
                {/* Genre Tab */}
                <button
                  onClick={() => setActiveFilterTab(activeFilterTab === 'genre' ? null : 'genre')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                    activeFilterTab === 'genre' ? 'bg-white/20 text-white' : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Film size={16} />
                  <span>Genre</span>
                  {selectedGenres.length > 0 && (
                    <span className="bg-white text-teal-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {selectedGenres.length}
                    </span>
                  )}
                  <ChevronDown size={14} className={`transition-transform ${activeFilterTab === 'genre' ? 'rotate-180' : ''}`} />
                </button>

                {/* Rating Tab */}
                <button
                  onClick={() => setActiveFilterTab(activeFilterTab === 'rating' ? null : 'rating')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                    activeFilterTab === 'rating' ? 'bg-white/20 text-white' : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Star size={16} />
                  <span>Rating</span>
                  {minRating > 0 && (
                    <span className="bg-white text-teal-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">1</span>
                  )}
                  <ChevronDown size={14} className={`transition-transform ${activeFilterTab === 'rating' ? 'rotate-180' : ''}`} />
                </button>

                {/* Year Tab */}
                <button
                  onClick={() => setActiveFilterTab(activeFilterTab === 'year' ? null : 'year')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                    activeFilterTab === 'year' ? 'bg-white/20 text-white' : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Calendar size={16} />
                  <span>Year</span>
                  {(yearRange[0] > 1920 || yearRange[1] < new Date().getFullYear()) && (
                    <span className="bg-white text-teal-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">1</span>
                  )}
                  <ChevronDown size={14} className={`transition-transform ${activeFilterTab === 'year' ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters - Always visible */}
          {hasActiveFilters && (
            <div className="bg-slate-800/80 border-b border-slate-700">
              <div className="max-w-screen-xl mx-auto px-4 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400 text-sm font-medium">Active filters:</span>
                  {selectedGenres.map(genreId => (
                    <div key={genreId} className="flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/20 border border-cyan-500/50 rounded-full text-xs text-cyan-300">
                      <span>{GENRES[genreId]}</span>
                      <button onClick={() => toggleGenre(genreId)} className="hover:bg-white/10 rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                  {minRating > 0 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-full text-xs text-yellow-300">
                      <span>Rating: {minRating}+</span>
                      <button onClick={() => setMinRating(0)} className="hover:bg-white/10 rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  )}
                  {(yearRange[0] > 1920 || yearRange[1] < new Date().getFullYear()) && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/20 border border-cyan-500/50 rounded-full text-xs text-cyan-300">
                      <span>Year: {yearRange[0]} - {yearRange[1]}</span>
                      <button onClick={() => setYearRange([1920, new Date().getFullYear()])} className="hover:bg-white/10 rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  )}
                  <button onClick={clearFilters} className="flex items-center gap-1 px-2.5 py-1 text-slate-400 hover:text-white text-xs transition-colors">
                    <RotateCcw size={12} />
                    <span>Clear all</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dropdown Panels */}
          {activeFilterTab === 'genre' && availableGenres.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-slate-800/95 backdrop-blur-xl border-b border-slate-700 shadow-2xl z-50">
              <div className="max-w-screen-xl mx-auto p-4">
                <div className="flex flex-wrap gap-2">
                  {availableGenres.map(genreId => (
                    <button
                      key={genreId}
                      onClick={() => toggleGenre(genreId)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        selectedGenres.includes(genreId)
                          ? 'bg-cyan-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {GENRES[genreId]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeFilterTab === 'rating' && (
            <div className="absolute top-full left-0 right-0 bg-slate-800/95 backdrop-blur-xl border-b border-slate-700 shadow-2xl z-50">
              <div className="max-w-screen-xl mx-auto p-5 space-y-4">
                <label className="text-sm text-slate-400 block">Minimum Rating: {minRating}</label>
                <input
                  type="range"
                  min="0"
                  max="9"
                  step="0.5"
                  value={minRating}
                  onChange={(e) => setMinRating(parseFloat(e.target.value))}
                  className="w-full max-w-md accent-yellow-500"
                />
                <div className="flex flex-wrap gap-2">
                  {[6, 7, 7.5, 8, 8.5, 9].map((r) => (
                    <button
                      key={r}
                      onClick={() => setMinRating(r)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        minRating === r ? 'bg-yellow-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {r}+
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeFilterTab === 'year' && (
            <div className="absolute top-full left-0 right-0 bg-slate-800/95 backdrop-blur-xl border-b border-slate-700 shadow-2xl z-50">
              <div className="max-w-screen-xl mx-auto p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="1920"
                    max={new Date().getFullYear()}
                    value={yearRange[0]}
                    onChange={(e) => setYearRange([parseInt(e.target.value), yearRange[1]])}
                    className="w-24 px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-cyan-500 outline-none"
                  />
                  <span className="text-slate-500">to</span>
                  <input
                    type="number"
                    min="1920"
                    max={new Date().getFullYear()}
                    value={yearRange[1]}
                    onChange={(e) => setYearRange([yearRange[0], parseInt(e.target.value)])}
                    className="w-24 px-3 py-2 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-cyan-500 outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: '2025', range: [2025, 2025] },
                    { label: '2024', range: [2024, 2024] },
                    { label: '2020s', range: [2020, 2029] },
                    { label: '2010s', range: [2010, 2019] },
                    { label: '2000s', range: [2000, 2009] },
                    { label: '90s', range: [1990, 1999] },
                    { label: '80s', range: [1980, 1989] },
                    { label: 'Classic', range: [1920, 1979] },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => setYearRange([preset.range[0], preset.range[1]])}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        yearRange[0] === preset.range[0] && yearRange[1] === preset.range[1]
                          ? 'bg-cyan-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results info with Sort dropdown - like home page */}
      {allMovies.length > 0 && (
        <div className="max-w-screen-xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">
              {movies.length} movies found
            </p>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'rating' | 'date' | 'title')}
                className="bg-slate-800 text-cyan-400 text-sm font-medium px-3 py-2 rounded-lg border border-slate-700 focus:border-cyan-500 outline-none cursor-pointer"
              >
                <option value="rating">Highest Rated</option>
                <option value="date">Newest First</option>
                <option value="title">A-Z</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Movie Grid */}
      {allMovies.length > 0 && (
        <div className="max-w-screen-xl mx-auto px-4 pb-8">

            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {movies.map((movie) => (
                  <Link
                    key={`act-${movie.id}`}
                    to={`/movie/${movie.id}`}
                    className="group"
                  >
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-800 mb-2 relative">
                      {movie.poster_path ? (
                        <img
                          src={getPosterUrl(movie.poster_path, 'medium')}
                          alt={movie.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-600">
                          <span className="text-4xl">🎬</span>
                        </div>
                      )}
                      {movie.vote_average > 0 && (
                        <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded flex items-center gap-1">
                          <Star size={12} className="text-yellow-400" />
                          <span className="text-white text-xs font-bold">{movie.vote_average.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                      {movie.title}
                    </h3>
                    {movie.character && (
                      <p className="text-xs text-slate-500 truncate">as {movie.character}</p>
                    )}
                    {movie.release_date && (
                      <p className="text-xs text-slate-600">{movie.release_date.split('-')[0]}</p>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <p>No movies match your filters</p>
                <button
                  onClick={clearFilters}
                  className="mt-2 text-cyan-400 hover:text-cyan-300"
                >
                  Clear filters
                </button>
              </div>
            )}
        </div>
      )}
    </div>
  );
}
